<template>
  <v-navigation-drawer
    v-model="internalShow"
    right
    temporary
    fixed
    :width="$vuetify.breakpoint.lgAndDown ? '450px' : '550px'"
    id="drawer"
  >
    <v-row class="pa-5">
      <v-col cols="12" v-if="!qAppId">
        <v-select 
          class="mx-3" 
          :items="apps" 
          item-text="name" 
          item-value="qAppId" 
          v-model="selectedAppId"
          prepend-inner-icon="apps"
          label="Application"
          outlined
          hide-details
        ></v-select>
      </v-col>
      <v-col cols="12">
        <v-combobox
          v-model="terms"
          :items="typesList"
          :label="$t('Qlik.Drawer.smartSearch')"
          :search-input.sync="search"
          class="mx-3"
          prepend-inner-icon="manage_search"
          chips
          deletable-chips
          hide-details
          multiple
          no-filter
          outlined
          small-chips
          hide-selected
        >
          <template v-slot:prepend-item>
            <v-list-item v-if="search" key="hint-confirm">
              <v-list-item-content>
                <v-list-item-title v-html="$t('Qlik.Drawer.hintSearchAdd', {search})" />
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-combobox>
      </v-col>
      <template v-if="loading">
        <v-col cols="12" class="mt-10">
          <v-card width="100%" class="text-center" flat color="transparent">
            <v-progress-circular
            size="250"
            color="primary"
            indeterminate
            width="20"
          ></v-progress-circular>
          </v-card>
        </v-col>
      </template>
      <template v-else>
        <v-col v-for="object in currentObjectList" :key="object.id" cols="12">
          <v-hover v-slot:default="{ hover }">
            <v-card 
              class="mx-3 py-2 objectDrawerCard"
              :elevation="hover ? 12 : 2"
              @click.capture.stop="selectObject(object)"
            >
              <QlikBox 
                :objectId="object.id" 
                :integrator="$qlik[useApp].integrator" 
                height="150px" 
                noSelections 
                noInteraction 
              />
            </v-card>
          </v-hover>
        </v-col>
        <v-col class="text-center my-2" cols="12" >
          <v-pagination
            v-model="page"
            :length="totalPages"
          ></v-pagination>
        </v-col>
      </template>
    </v-row>
  </v-navigation-drawer>
</template>

<script>
import QlikBox from '@/components/Qlik/QlikBox'
import { enabledTypes } from '@/config/qlik'
import { mapState } from 'vuex'
export default {
  components: { QlikBox },
  props: {
    show: {type: Boolean, required: false, default: false},
    /**
     * To force for a specific app
     */
    qAppId: {type: String, required: false, default: null}
  },
  data: () => ({
    selectedAppId: null,
    page: 1,
    types: [],
    objects: [],
    loading: false,
    search: '',
    terms: []
  }),
  watch: {
    useApp: {
      immediate: true,
      handler: async function (value) {
        if (value) {
          this.loading = true
          this.page = 1
          this.search = ''
          this.terms = []
          await this.loadAppObjects()
          this.loading = false
        }
      }
    },
    terms (value) {
      this.page = 1
      value?.length ? this.applySearch(value) : this.loadAppObjects()
      // Hack to auto close search menu on new search without side effects
      document.getElementById('drawer').click()
    }
  },
  computed: {
    ...mapState('qlik', [
      'apps'
    ]),
    useApp() {
      return this.qAppId || this.selectedAppId
    },
    internalShow: {
      get() {
        return this.show
      },
      set(value) {
        if (!value) this.close()
      }
    },
    typesList () {
      return [{ header: this.$t('Qlik.Drawer.hintSearch') }, ...this.types]
    },
    objectsListFormatted () {
      return this.objects.map(obj => ({
        ...obj,
        id: obj.qId
      }))
    },
    currentObjectList () {
      if (!this.objectsListFormatted.length) return []
      return this.objectsListFormatted.slice(this.page * 10 - 10, this.page * 10)
    },
    totalPages () {
      return Math.ceil(this.objectsListFormatted.length / 10)
    }
  },
  methods: {
    selectObject (object) {
      this.$emit('selected', {qObjectId: object.id, qAppId: this.useApp})
    },
    close () {
      this.filter = null
      this.$emit('closed')
    },
    async loadAppObjects () {
      const availableTypes = Object.keys(enabledTypes)
      const qlikObjects = await this.$qlik[this.useApp].enigmaDoc.getObjects({
        "qOptions": {
          "qTypes": availableTypes,
          "qIncludeSessionObjects": true,
          "qData": {}
        }
      })

      this.objects = qlikObjects.map(obj => obj.qInfo)
      this.types = availableTypes.map(type => ({
        text: this.$t('Qlik.Types.' + type),
        value: type,
        disabled: !this.objects.some(obj => obj.qType === type)
      }))
    },
    async applySearch () {
      const parsedTerms = this.terms.map(term  => (typeof term === 'object') ? term.value : term)
      let items = (await this.$qlik[this.useApp].enigmaDoc.searchObjects({
        "qOptions": {
          "qCharEncoding": 0
        },
        "qTerms": parsedTerms,
        "qPage": {
          "qOffset": 0,
          "qCount": 100,
          "qMaxNbrFieldMatches": 0
        }
      }))?.qSearchGroupArray[0]?.qItems || []

      this.objects = items.map(item => ({qId: item.qIdentifier, qType: this.filter}))
    }
  },
  created () {
    if (!this.useApp && this.apps.length) this.selectedAppId = this.apps[0].qAppId
    this.filter = this.type
  }
}
</script>

<style>
.objectDrawerCard * {
  cursor: pointer!important;
}
</style>